import React, { Component } from "react";
import propTypes from "prop-types";
import { observer, inject } from "mobx-react";
import withRouter from "../../components/withRouter";
import t from "../../service/translate.service";
import LoadingPageOverlay from "../../components/LoadingPageOverlay";
import { Alert, Button, Modal, Spin, message, Table, Input, Pagination, Select } from "antd";
import { EyeOutlined, DownloadOutlined, RightOutlined, DownOutlined, LockOutlined, TagsOutlined, SearchOutlined } from "@ant-design/icons";
import {
  extractError,
  extractErrorFromSap,
} from "../../service/response.service";
import { appFeatureEnabled } from "../../service/appFeatures.service";
import NoContent from "../../components/NoContent";
import { ContractsImage } from "../../components/icons";
import { saveAs } from "file-saver";

const FILTER_OPTIONS_MAX_TEXT = 50;
const FILTER_OPTIONS_MAX_DATES_AND_AMOUNTS = 30;

/** Page-size Select that opens upward so the dropdown is not covered by UI below. */
const PaginationSizeSelect = (props) => <Select {...props} placement="topLeft" />;
PaginationSizeSelect.Option = Select.Option;

class CondoInvoicesList extends Component {
  static propTypes = {
    contractStore: propTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      initialLoading: true,
      errorMsg: "",
      previewModalVisible: false,
      previewPdfUrl: null,
      previewLoading: false,
      previewError: null,
      expandedAllocationKeys: {},
      expandedBillingUnits: {},
      searchInput: "",
      searchTerm: "",
      allocationPage: 1,
      allocationPageSize: 15,
      /** Selected year to filter tables by payment date (null = show all). */
      selectedYear: null,
      /** Pagination per billing-unit table: { [billingUnitCompositeKey]: { current, pageSize } }. */
      billingTablePagination: {},
    };
    this.searchDebounceTimer = null;
    this._mounted = { current: true };
  }

  componentWillUnmount() {
    this._mounted.current = false;
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    if (this.state.previewPdfUrl) URL.revokeObjectURL(this.state.previewPdfUrl);
    if (this.state.previewPdfBlobs?.length) {
      this.state.previewPdfBlobs.forEach(({ url }) => URL.revokeObjectURL(url));
    }
  }

  async componentDidMount() {
    window.scrollTo(0, 0);

    const contractNumber = this.props.useParams?.contractNumber;
    
    try {
      // Get contract and role information
      await this.props.contractStore.initContracts();
      const selectedContract = this.props.contractStore.activeContracts?.find(
        (c) => c.contractNumber === contractNumber
      );
      
      if (!selectedContract) {
        this.setState({ 
          initialLoading: false,
          errorMsg: t.ERROR || "Contract not found"
        });
        return;
      }

      const role = this.props.contractStore.role;

      // Check if user has permission to view invoices
      if (!appFeatureEnabled("condoReceiptInspectionFeaturesEnabled", [selectedContract]) ||
          !appFeatureEnabled("pageCondoInvoicesVisibleTo", [{...selectedContract, role}])) {
        this.setState({ 
          initialLoading: false,
          errorMsg: "Access denied"
        });
        return;
      }

      // Fetch invoices
      await this.fetchInvoices();
      this.setState({ initialLoading: false });
    } catch (err) {
      this.setState({
        initialLoading: false,
        errorMsg:
          extractErrorFromSap(err) ||
          t[extractError(err)] ||
          t["ERROR"],
      });
    }
  }

  fetchInvoices = async () => {
    const contractNumber = this.props.useParams?.contractNumber;
    
    try {
      // Fetch all invoices - uses default size of 100k from store method
      await this.props.contractStore.fetchCondoInvoices(contractNumber);
    } catch (err) {
      this.setState({ 
        errorMsg: extractErrorFromSap(err) || t[extractError(err)] || t["ERROR"]
      });
    }
  };

  formatCurrency = (amount, currency = "EUR") => {
    const numAmount = parseFloat(amount || 0);
    return `${numAmount.toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${currency}`;
  };

  formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("de-DE");
  };

  /** Escape special regex characters in search term for safe RegExp use */
  escapeRegex = (str) => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  /** Returns true if the line item matches the search term in any searchable field */
  lineItemMatches = (lineItem, searchTerm) => {
    if (!searchTerm || !searchTerm.trim()) return false;
    const term = searchTerm.trim().toLowerCase();
    const fields = [
      lineItem.supplier,
      lineItem.supplierInvoiceId,
      lineItem.invoiceText,
      lineItem.description,
      lineItem.invoiceDate ? this.formatDate(lineItem.invoiceDate) : "",
      lineItem.paymentDate ? this.formatDate(lineItem.paymentDate) : "",
      lineItem.amount != null ? String(lineItem.amount) : "",
      lineItem.amount != null ? this.formatCurrency(lineItem.amount) : "",
    ];
    return fields.some((val) => val && String(val).toLowerCase().includes(term));
  };

  /** Wrap matching substrings in <mark> for highlight; returns React nodes */
  highlightText = (text, searchTerm) => {
    const str = text != null && text !== "" ? String(text) : "-";
    if (!searchTerm || !searchTerm.trim()) return str;
    const term = this.escapeRegex(searchTerm.trim());
    const regex = new RegExp(`(${term})`, "gi");
    const parts = str.split(regex);
    // With capturing group, split includes matches at odd indices
    return parts.map((part, i) =>
      i % 2 === 1 ? (
        <mark key={i} className="condo-invoice-search-highlight">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  /** Compute which allocation keys and billing units to expand when search is active */
  getSearchExpandedState = (allocationData, searchTerm) => {
    if (!searchTerm || !searchTerm.trim())
      return { expandedAllocationKeys: {}, expandedBillingUnits: {} };
    const term = searchTerm.trim().toLowerCase();
    const expandedAllocationKeys = {};
    const expandedBillingUnits = {};
    allocationData.forEach(({ allocationKey, billingUnits }) => {
      billingUnits.forEach(({ billingUnitKey, lineItems }) => {
        const hasMatch = lineItems.some((item) => this.lineItemMatches(item, searchTerm));
        if (hasMatch) {
          expandedAllocationKeys[allocationKey] = true;
          expandedBillingUnits[`${allocationKey}-${billingUnitKey}`] = true;
        }
      });
    });
    return { expandedAllocationKeys, expandedBillingUnits };
  };

  handleSearch = (value) => {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    if (!value || !value.trim()) {
      this.setState({
        searchInput: value,
        searchTerm: "",
        expandedAllocationKeys: {},
        expandedBillingUnits: {},
        allocationPage: 1,
      });
      return;
    }
    this.setState({ searchInput: value });
    this.searchDebounceTimer = setTimeout(() => {
      if (!this._mounted.current) return;
      const allocationData = this.getInvoicesByAllocation();
      const dataForExpand = this.state.selectedYear != null
        ? this.getAllocationDataFilteredByPaymentYear(allocationData, this.state.selectedYear)
        : allocationData;
      const { expandedAllocationKeys, expandedBillingUnits } = this.getSearchExpandedState(
        dataForExpand,
        value
      );
      this.setState({
        searchTerm: value,
        expandedAllocationKeys,
        expandedBillingUnits,
        allocationPage: 1,
      });
    }, 300);
  };

  /**
   * Returns invoiceId strings for each document of this line item (one per arcDocId).
   * BTP/SAP expects invoiceId in the shape: vorbl-objectId-arcDocId (split by '-').
   * vorbl = prepaidNumber, objectId = objectId, arcDocId = one of arcDocIds.
   */
  getDocumentIds = (lineItem) => {
    const { prepaidNumber, objectId, arcDocIds } = lineItem;
    if (!prepaidNumber || !objectId || !arcDocIds?.length) return [];
    return arcDocIds.map((arcDocId) => `${prepaidNumber}-${objectId}-${arcDocId}`);
  };

  /**
   * Fetches PDFs for all documents of this invoice. Tries each arcDocId; SAP may not have
   * a document for all. Returns only successful blobs and any partial errors.
   */
  fetchInvoicePdf = async (lineItem) => {
    const contractNumber = this.props.useParams?.contractNumber;
    const documentIds = this.getDocumentIds(lineItem);
    if (!documentIds.length) {
      return { blobs: [], partialErrors: [] };
    }
    const results = await Promise.allSettled(
      documentIds.map((invoiceId) =>
        this.props.contractStore.fetchCondoInvoicePdf(contractNumber, invoiceId)
      )
    );
    const blobs = [];
    const partialErrors = [];
    const baseName = `invoice_${lineItem.supplierInvoiceId ?? "document"}`;
    results.forEach((outcome, index) => {
      if (outcome.status === "fulfilled" && outcome.value?.file) {
        const pdfData = outcome.value;
        const blob = this.base64ToBlob(pdfData.file, "application/pdf");
        const fileName =
          pdfData.fileName ||
          (documentIds.length > 1 ? `${baseName}_${index + 1}.pdf` : `${baseName}.pdf`);
        blobs.push({ blob, fileName });
      } else if (outcome.status === "rejected") {
        partialErrors.push({ index: index + 1, error: outcome.reason });
      }
    });
    return { blobs, partialErrors };
  };

  base64ToBlob = (base64, contentType = '') => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  };

  handlePreview = async (lineItem) => {
    this.setState({
      previewModalVisible: true,
      previewLoading: true,
      previewError: null,
      previewPdfUrl: null,
      previewPdfBlobs: null,
      previewSelectedIndex: 0,
      previewPartialErrors: [],
    });

    try {
      const { blobs, partialErrors } = await this.fetchInvoicePdf(lineItem);
      if (!blobs.length) {
        const triedCount = (lineItem.arcDocIds?.length ?? 0) || 1;
        const errorMessage =
          partialErrors.length > 0
            ? extractErrorFromSap(partialErrors[0].error) ||
              t.CONDO_INVOICE_NOT_FOUND ||
              t.CONDO_INVOICE_PDF_ERROR ||
              `No document could be found (tried ${triedCount} document(s)).`
            : t.CONDO_INVOICE_NOT_FOUND || t.CONDO_INVOICE_PDF_ERROR;
        this.setState({ previewLoading: false, previewError: errorMessage });
        return;
      }
      const previewPdfBlobs = blobs.map(({ blob, fileName }) => ({
        url: URL.createObjectURL(blob),
        fileName,
      }));
      this.setState({
        previewPdfBlobs,
        previewPartialErrors: partialErrors,
        previewLoading: false,
        previewSelectedIndex: 0,
      });
    } catch (error) {
      const errorMessage =
        extractErrorFromSap(error) || t.CONDO_INVOICE_NOT_FOUND || t.CONDO_INVOICE_PDF_ERROR;
      this.setState({ previewLoading: false, previewError: errorMessage });
    }
  };

  handlePreviewSelectDocument = (index) => {
    this.setState({ previewSelectedIndex: index });
  };

  handleDownload = async (lineItem) => {
    try {
      const { blobs, partialErrors } = await this.fetchInvoicePdf(lineItem);
      if (!blobs.length) {
        const triedCount = (lineItem.arcDocIds?.length ?? 0) || 1;
        const msg =
          partialErrors.length > 0
            ? extractErrorFromSap(partialErrors[0].error) ||
              (t.CONDO_INVOICE_NOT_FOUND != null ? t.CONDO_INVOICE_NOT_FOUND : `No document could be found (tried ${triedCount} document(s)).`)
            : t.CONDO_INVOICE_NOT_FOUND || "Invoice document could not be found or downloaded";
        message.error(msg);
        return;
      }
      blobs.forEach(({ blob, fileName }) => saveAs(blob, fileName));
      if (partialErrors.length > 0 && blobs.length > 0) {
        message.info(
          t.CONDO_INVOICE_PARTIAL_DOWNLOAD != null
            ? t.CONDO_INVOICE_PARTIAL_DOWNLOAD
            : `${blobs.length} document(s) downloaded; some could not be loaded.`
        );
      }
    } catch (error) {
      console.error("Error downloading PDF:", error);
      const errorMessage =
        extractErrorFromSap(error) || t.CONDO_INVOICE_NOT_FOUND || "Invoice document could not be found or downloaded";
      message.error(errorMessage);
    }
  };

  handleClosePreview = () => {
    const { previewPdfUrl, previewPdfBlobs } = this.state;
    if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
    if (previewPdfBlobs?.length) {
      previewPdfBlobs.forEach(({ url }) => URL.revokeObjectURL(url));
    }
    this.setState({
      previewModalVisible: false,
      previewPdfUrl: null,
      previewPdfBlobs: null,
      previewSelectedIndex: 0,
      previewError: null,
      previewPartialErrors: [],
    });
  };

  /**
   * Normalize a line item so table and sorters always see the same field names.
   * API may return documentDate/postingDate, supplierNumber, referenceNumber/invoiceId, documentId.
   * We map to invoiceDate, paymentDate, supplier, supplierInvoiceId, objectId so columns and sorters work.
   * Supports single arcDocId or multiple arcDocIds (array); normalized to arcDocIds array for PDF fetch.
   */
  normalizeLineItem = (raw) => {
    if (!raw || typeof raw !== 'object') return raw;
    const arcDocIds = Array.isArray(raw.arcDocIds)
      ? raw.arcDocIds
      : raw.arcDocId != null
        ? [raw.arcDocId]
        : [];
    return {
      ...raw,
      invoiceDate: raw.invoiceDate ?? raw.documentDate,
      paymentDate: raw.paymentDate ?? raw.postingDate,
      supplier: raw.supplier ?? raw.supplierNumber ?? raw.supplierName,
      supplierInvoiceId: raw.supplierInvoiceId ?? raw.referenceNumber ?? raw.invoiceId,
      invoiceText: raw.invoiceText || raw.description || "",
      objectId: raw.objectId ?? raw.documentId ?? raw.arcDocId,
      arcDocId: raw.arcDocId ?? arcDocIds[0],
      arcDocIds,
    };
  };

  /**
   * Returns allocation key entries from invoices object (new format).
   * Structure: allocation key (e.g. "1061") → billing unit key (e.g. "1") → { billingUnitDescription, amount, lineItems }.
   * Line items are normalized so sort/display use consistent field names.
   */
  getInvoicesByAllocation = () => {
    const { condoInvoices } = this.props.contractStore;
    const invoices = condoInvoices?.invoices;
    if (!invoices || typeof invoices !== 'object') return [];

    return Object.entries(invoices).map(([allocationKey, allocationData]) => {
      const { allocationKeyDescription, amount } = allocationData;
      const billingUnits = [];
      Object.entries(allocationData).forEach(([key, value]) => {
        if (key === 'allocationKeyDescription' || key === 'amount') return;
        if (value && typeof value === 'object' && Array.isArray(value.lineItems)) {
          billingUnits.push({
            billingUnitKey: key,
            billingUnitDescription: value.billingUnitDescription,
            amount: value.amount,
            lineItems: (value.lineItems || []).map((item, i) => ({
              ...this.normalizeLineItem(item),
              _rowId: `${allocationKey}-${key}-${i}`,
            })),
          });
        }
      });
      return {
        allocationKey,
        allocationKeyDescription: allocationKeyDescription || allocationKey,
        amount,
        billingUnits,
      };
    });
  };

  /** Flatten all line items from allocation data. */
  getAllLineItems = (allocationData) => {
    const items = [];
    allocationData.forEach(({ billingUnits }) => {
      billingUnits.forEach(({ lineItems }) => {
        items.push(...lineItems);
      });
    });
    return items;
  };

  /** Year from payment date (or null if missing/invalid). */
  getPaymentDateYear = (item) => {
    const date = item.paymentDate;
    if (!date) return null;
    const y = new Date(date).getFullYear();
    return Number.isNaN(y) ? null : y;
  };

  /** Distinct years present in payment dates, sorted descending (for year filter dropdown). */
  getYearsFromPaymentDates = (allocationData) => {
    const years = new Set();
    this.getAllLineItems(allocationData).forEach((item) => {
      const y = this.getPaymentDateYear(item);
      if (y != null) years.add(y);
    });
    return [...years].sort((a, b) => b - a);
  };

  /**
   * Filter allocation data by payment date year. When year is set, only line items whose
   * payment date falls in that year are kept; amounts are recomputed. Billing units and
   * allocations with no matching items are omitted.
   */
  getAllocationDataFilteredByPaymentYear = (allocationData, year) => {
    if (year == null) return allocationData;
    return allocationData
      .map(({ allocationKey, allocationKeyDescription, billingUnits }) => {
        const filteredBillingUnits = billingUnits
          .map(({ billingUnitKey, billingUnitDescription, lineItems }) => {
            const filteredItems = lineItems.filter(
              (item) => this.getPaymentDateYear(item) === year
            );
            if (filteredItems.length === 0) return null;
            const amount = filteredItems.reduce(
              (sum, item) => sum + this.parseAmountForSort(item.amount),
              0
            );
            return {
              billingUnitKey,
              billingUnitDescription,
              amount,
              lineItems: filteredItems,
            };
          })
          .filter(Boolean);
        if (filteredBillingUnits.length === 0) return null;
        const amount = filteredBillingUnits.reduce((sum, bu) => sum + bu.amount, 0);
        return {
          allocationKey,
          allocationKeyDescription,
          amount,
          billingUnits: filteredBillingUnits,
        };
      })
      .filter(Boolean);
  };

  /** Table locale for filter dropdown buttons (shared by billing-unit tables). */
  getTableFilterLocale = () => ({
    filterConfirm: t.CONDO_INVOICES_FILTER_APPLY ?? t.FILTER_APPLY ?? "OK",
    filterReset: t.CONDO_INVOICES_FILTER_RESET ?? t.FILTER_RESET ?? "Reset",
  });

  /** Row class for search highlight (shared by year and billing-unit tables). */
  getLineItemRowClassName = (record) =>
    this.state.searchTerm && this.lineItemMatches(record, this.state.searchTerm)
      ? "condo-invoice-row-match"
      : "";

  /** Unique row key for a line item. Use pre-assigned _rowId (set when building data) to avoid deprecated index param. */
  getLineItemRowKey = (record) => {
    if (record._rowId) return record._rowId;
    const { prepaidNumber, arcDocId, objectId } = record;
    if (prepaidNumber != null && arcDocId != null && objectId != null) {
      return `${prepaidNumber}-${arcDocId}-${objectId}`;
    }
    return `line-${record.supplierInvoiceId ?? record.supplier ?? Math.random()}`;
  };

  toggleAllocationKey = (allocationKey) => {
    this.setState((prev) => ({
      expandedAllocationKeys: {
        ...prev.expandedAllocationKeys,
        [allocationKey]: !prev.expandedAllocationKeys[allocationKey],
      },
    }));
  };

  toggleBillingUnit = (allocationKey, billingUnitKey) => {
    const key = `${allocationKey}-${billingUnitKey}`;
    this.setState((prev) => ({
      expandedBillingUnits: {
        ...prev.expandedBillingUnits,
        [key]: !prev.expandedBillingUnits[key],
      },
    }));
  };

  /**
   * Sort helpers for table columns. Return { compare } for Ant Design Table (asc/desc handled by Table).
   * compare(a, b) returns negative/zero/positive; Table passes sortOrder as 3rd arg if needed.
   * Line item shape: supplier, supplierInvoiceId, invoiceText/description, invoiceDate, paymentDate, amount.
   * API may use documentDate/postingDate; pass fallbackField for that.
   */
  sortByDate = (field, fallbackField) => ({
    compare: (a, b) => {
      const ts = (r) => {
        const val = r[field] ?? (fallbackField ? r[fallbackField] : null);
        const t = new Date(val || 0).getTime();
        return Number.isNaN(t) ? 0 : t;
      };
      return ts(a) - ts(b);
    },
  });

  sortByString = (field) => ({
    compare: (a, b) =>
      String(a[field] ?? "").localeCompare(String(b[field] ?? ""), undefined, { sensitivity: "base" }),
  });

  /**
   * Compare function for a column key. Used to sort the invoice line data (the actual table rows).
   * Each row is a normalized line item: supplier, supplierInvoiceId, invoiceDate, paymentDate, amount, etc.
   */
  getCompareFnForColumn = (columnKey) => {
    switch (columnKey) {
      case "supplier":
        return this.sortByString("supplier").compare;
      case "supplierInvoiceId":
        return this.sortByString("supplierInvoiceId").compare;
      case "description":
        return (a, b) =>
          this.getInvoiceText(a).localeCompare(this.getInvoiceText(b), undefined, { sensitivity: "base" });
      case "invoiceDate":
        return this.sortByDate("invoiceDate", "documentDate").compare;
      case "paymentDate":
        return this.sortByDate("paymentDate", "postingDate").compare;
      case "amount":
        return (a, b) =>
          this.parseAmountForSort(a.amount) - this.parseAmountForSort(b.amount);
      default:
        return null;
    }
  };

  /** Distinct values for a column (for filter options). */
  getColumnFilterOptions = (lineItems, field, formatter, maxOptions = FILTER_OPTIONS_MAX_TEXT) => {
    const values = lineItems
      .map((item) => {
        const raw = item[field];
        return raw == null ? null : formatter ? formatter(raw) : raw;
      })
      .filter((v) => v != null && v !== "");
    const distinct = [...new Set(values)];
    const sorted = typeof distinct[0] === "number" ? distinct.sort((a, b) => a - b) : distinct.sort();
    const limited = maxOptions ? sorted.slice(0, maxOptions) : sorted;
    return limited.map((v) => ({ text: String(v), value: v }));
  };

  /**
   * Parse amount to number for sorting. Handles negative values and string numbers (e.g. "-123.45").
   * European format "1.234,56" / "-1.234,56" is normalized (strip dots, comma -> dot). Returns 0 for null/undefined/NaN.
   */
  parseAmountForSort = (val) => {
    if (val == null || val === "") return 0;
    if (typeof val === "number" && !Number.isNaN(val)) return val;
    const s = String(val).trim();
    if (s === "") return 0;
    // Try direct parse first (e.g. -123.45)
    let num = Number(s);
    if (!Number.isNaN(num)) return num;
    // European: "1.234,56" or "-1.234,56" -> remove dots, replace comma with dot
    const normalized = s.replace(/\./g, "").replace(",", ".");
    num = Number(normalized);
    return Number.isNaN(num) ? 0 : num;
  };

  /** Amount as stable string key (2 decimals) for filter value and matching. */
  amountToFilterKey = (val) => {
    const num = Number(val);
    if (val == null || val === "" || Number.isNaN(num)) return null;
    return (Math.round(num * 100) / 100).toFixed(2);
  };

  /** Filter options for invoice text column: same canonical value (invoiceText ?? description) used for display and filter. */
  getInvoiceTextFilterOptions = (lineItems, maxOptions = FILTER_OPTIONS_MAX_TEXT) => {
    const values = lineItems
      .map((item) => this.getInvoiceText(item))
      .filter((v) => v !== "");
    const distinct = [...new Set(values)].sort();
    const limited = maxOptions ? distinct.slice(0, maxOptions) : distinct;
    return limited.map((v) => ({ text: v, value: v }));
  };

  /** Filter options for amount column (one option per distinct amount). */
  getAmountFilterOptions = (lineItems, maxOptions = FILTER_OPTIONS_MAX_DATES_AND_AMOUNTS) => {
    const keys = lineItems.map((r) => this.amountToFilterKey(r.amount)).filter(Boolean);
    const distinct = [...new Set(keys)].sort((a, b) => Number(a) - Number(b));
    return distinct.slice(0, maxOptions).map((key) => ({
      text: this.formatCurrency(Number(key)),
      value: key,
    }));
  };

  /** Normalize for filter match: trim and stringify. Used for both option values and row values. */
  filterValue = (v) => (v == null || v === "" ? "" : String(v).trim());

  /** Canonical "invoice text" for a line item (display and filter). API may use invoiceText or description. */
  getInvoiceText = (record) => this.filterValue(record.invoiceText ?? record.description);

  /**
   * Value used for filtering a row by column (must match filter option `value`).
   * Used by Ant Design Table's onFilter(value, record).
   */
  getRecordFilterKey = (record, columnKey) => {
    switch (columnKey) {
      case "supplier":
        return this.filterValue(record.supplier);
      case "supplierInvoiceId":
        return this.filterValue(record.supplierInvoiceId);
      case "description":
        return this.getInvoiceText(record);
      case "invoiceDate":
        return this.formatDate(record.invoiceDate);
      case "paymentDate":
        return this.formatDate(record.paymentDate);
      case "amount":
        return this.amountToFilterKey(record.amount);
      default:
        return null;
    }
  };

  /** Pagination config for each line-items table. */
  getLineItemsPagination = () => ({
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50"],
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} ${t.CONDO_INVOICES_OF || "of"} ${total}`,
    selectComponentClass: PaginationSizeSelect,
  });

  /** Get controlled pagination for a billing-unit table so page-size changer works. */
  getBillingUnitTablePagination = (tableId) => {
    const stored = this.state.billingTablePagination[tableId];
    const current = stored?.current ?? 1;
    const pageSize = stored?.pageSize ?? 10;
    return {
      ...this.getLineItemsPagination(),
      current,
      pageSize,
      onChange: (page, newPageSize) =>
        this.setState((prev) => ({
          billingTablePagination: {
            ...prev.billingTablePagination,
            [tableId]: {
              current: page,
              pageSize: newPageSize ?? pageSize,
            },
          },
        })),
    };
  };

  /**
   * Line item columns. Uses Ant Design Table's built-in filters (filters + onFilter) and sorter.
   * Year table and billing unit tables: includeColumnFilters = true for column filter icons/dropdowns.
   */
  getLineItemColumns = (lineItems, searchTerm, options = {}) => {
    const { includeColumnFilters = true } = options;
    const filterOpt = (field, formatter, max) =>
      this.getColumnFilterOptions(lineItems, field, formatter, max);

    const filterCol = (col, columnKey, filterOptions) => {
      if (!includeColumnFilters || !filterOptions?.length) return col;
      return {
        ...col,
        filters: filterOptions,
        onFilter: (value, record) => this.getRecordFilterKey(record, columnKey) === value,
      };
    };

    return [
      filterCol(
        {
          title: t.CONDO_INVOICES_SUPPLIER,
          dataIndex: "supplier",
          key: "supplier",
          render: (text) => this.highlightText(text || "-", searchTerm),
          sorter: this.getCompareFnForColumn("supplier"),
        },
        "supplier",
        filterOpt("supplier", null, FILTER_OPTIONS_MAX_TEXT)
      ),
      filterCol(
        {
          title: t.CONDO_INVOICES_INVOICE_ID,
          dataIndex: "supplierInvoiceId",
          key: "supplierInvoiceId",
          render: (text) => this.highlightText(text || "-", searchTerm),
          sorter: this.getCompareFnForColumn("supplierInvoiceId"),
        },
        "supplierInvoiceId",
        filterOpt("supplierInvoiceId", null, FILTER_OPTIONS_MAX_TEXT)
      ),
      filterCol(
        {
          title: t.CONDO_INVOICES_DESCRIPTION,
          dataIndex: "description",
          key: "description",
          render: (_, record) => this.highlightText((record.invoiceText ?? record.description) || "-", searchTerm),
          sorter: this.getCompareFnForColumn("description"),
        },
        "description",
        this.getInvoiceTextFilterOptions(lineItems, FILTER_OPTIONS_MAX_TEXT)
      ),
      filterCol(
        {
          title: t.CONDO_INVOICES_INVOICE_DATE,
          dataIndex: "invoiceDate",
          key: "invoiceDate",
          render: (text) => this.highlightText(this.formatDate(text), searchTerm),
          sorter: this.getCompareFnForColumn("invoiceDate"),
        },
        "invoiceDate",
        filterOpt("invoiceDate", (d) => this.formatDate(d), FILTER_OPTIONS_MAX_DATES_AND_AMOUNTS)
      ),
      filterCol(
        {
          title: t.CONDO_INVOICES_PAYMENT_DATE,
          dataIndex: "paymentDate",
          key: "paymentDate",
          render: (text) => this.highlightText(this.formatDate(text), searchTerm),
          sorter: this.getCompareFnForColumn("paymentDate"),
        },
        "paymentDate",
        filterOpt("paymentDate", (d) => this.formatDate(d), FILTER_OPTIONS_MAX_DATES_AND_AMOUNTS)
      ),
      filterCol(
        {
          title: t.CONDO_INVOICES_AMOUNT,
          dataIndex: "amount",
          key: "amount",
          render: (text) => (
            <span style={{ textAlign: "right", fontWeight: "500" }}>
              {this.highlightText(text != null ? this.formatCurrency(text) : "-", searchTerm)}
            </span>
          ),
          sorter: this.getCompareFnForColumn("amount"),
        },
        "amount",
        this.getAmountFilterOptions(lineItems, FILTER_OPTIONS_MAX_DATES_AND_AMOUNTS)
      ),
      {
        title: t.CONDO_INVOICE_PREVIEW,
        key: "actions",
        align: "center",
        width: 120,
        render: (_, record) => (
          <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => this.handlePreview(record)}
              title={t.CONDO_INVOICE_PREVIEW}
            />
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={() => this.handleDownload(record)}
              title={t.CONDO_INVOICE_DOWNLOAD}
            />
          </div>
        ),
      },
    ];
  };

  render() {
    const {
      initialLoading,
      errorMsg,
      previewModalVisible,
      previewPdfUrl,
      previewPdfBlobs,
      previewSelectedIndex,
      previewLoading,
      previewError,
      previewPartialErrors,
      expandedAllocationKeys,
      expandedBillingUnits,
      searchInput,
      searchTerm,
      selectedYear,
    } = this.state;
    const { condoInvoicesError, condoInvoicesLoading } = this.props.contractStore;

    // Show full-page loader only on initial load
    if (initialLoading) {
      return <LoadingPageOverlay />;
    }

    if (errorMsg || condoInvoicesError) {
      return (
        <div className="contract-list">
          <Alert
            role="alert"
            aria-live="assertive"
            message={errorMsg || condoInvoicesError}
            type="error"
            style={{ marginBottom: "12px", marginTop: "12px" }}
          />
          <Button
            onClick={() => this.props.navigate(-1)}
            style={{ marginTop: "20px" }}
          >
            {t.BACK || "Back"}
          </Button>
        </div>
      );
    }

    const allocationData = this.getInvoicesByAllocation();
    const displayData = this.getAllocationDataFilteredByPaymentYear(allocationData, selectedYear);
    const hasInvoices = allocationData.length > 0;
    const totalAllocations = displayData.length;
    const allocationPage = this.state.allocationPage;
    const allocationPageSize = this.state.allocationPageSize;
    const paginatedAllocationData = displayData.slice(
      (allocationPage - 1) * allocationPageSize,
      allocationPage * allocationPageSize
    );

    return (
      <div className="contract-list">
        <div style={{ marginBottom: "20px" }}>
          <h2>{t.CONDO_INVOICES_TITLE}</h2>
          <p style={{ color: "#666" }}>
            {t.CONTRACT_NUMBER || "Contract Number"}:{" "}
            {this.props.useParams?.contractNumber}
          </p>
        </div>

        {condoInvoicesLoading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
          </div>
        ) : !hasInvoices ? (
          <NoContent
            message={t.CONDO_INVOICES_NO_DATA}
            image={ContractsImage}
          />
        ) : selectedYear != null && displayData.length === 0 ? (
          <NoContent
            message={t.CONDO_INVOICES_NO_DATA_FOR_YEAR != null ? t.CONDO_INVOICES_NO_DATA_FOR_YEAR.replace("{year}", selectedYear) : `No invoices with payment date in ${selectedYear}.`}
            image={ContractsImage}
          />
        ) : (
          <>
            <div style={{ marginBottom: "16px", display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
              <Input
                style={{ maxWidth: "400px" }}
                placeholder={t.CONDO_INVOICES_SEARCH_PLACEHOLDER || "Search invoices…"}
                prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                value={searchInput}
                onChange={(e) => this.handleSearch(e.target.value)}
                allowClear
              />
              <Select
                placeholder={t.CONDO_INVOICES_SELECT_YEAR || "Select year"}
                allowClear
                value={selectedYear ?? undefined}
                onChange={(year) => this.setState({ selectedYear: year ?? null, allocationPage: 1 })}
                style={{ minWidth: 140 }}
                options={this.getYearsFromPaymentDates(allocationData).map((y) => ({ label: String(y), value: y }))}
              />
            </div>
            {/* Hierarchy: allocation key (row) → billing unit (row) → line items Table per billing unit (filtered by payment date year when selected) */}
            <div className="condo-invoices-hierarchy" style={{ marginBottom: "16px" }}>
            {paginatedAllocationData.map(({ allocationKey, allocationKeyDescription, amount, billingUnits }) => {
              const isAllocationExpanded = expandedAllocationKeys[allocationKey];
              return (
                <div
                  key={allocationKey}
                  style={{
                    border: "1px solid #e8e8e8",
                    borderRadius: "8px",
                    marginBottom: "8px",
                    overflow: "hidden",
                  }}
                >
                  {/* Layer 1: Allocation key row */}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => this.toggleAllocationKey(allocationKey)}
                    onKeyDown={(e) => e.key === "Enter" && this.toggleAllocationKey(allocationKey)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "12px 16px",
                      backgroundColor: "#fafafa",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    <LockOutlined style={{ marginRight: "8px", fontSize: "14px", color: "#595959" }} />
                    {isAllocationExpanded ? (
                      <DownOutlined style={{ marginRight: "8px", fontSize: "12px" }} />
                    ) : (
                      <RightOutlined style={{ marginRight: "8px", fontSize: "12px" }} />
                    )}
                    <span style={{ fontWeight: 600, flex: 1 }}>
                      {allocationKeyDescription}
                    </span>
                    <span style={{ fontWeight: 500 }}>
                      {this.formatCurrency(amount)}
                    </span>
                  </div>

                  {/* Layer 2: Billing units (when allocation expanded) */}
                  {isAllocationExpanded && (
                    <div style={{ padding: "0 16px 16px", borderTop: "1px solid #e8e8e8" }}>
                      {billingUnits.map(({ billingUnitKey, billingUnitDescription, amount: buAmount, lineItems }) => {
                        const billingUnitCompositeKey = `${allocationKey}-${billingUnitKey}`;
                        const isBillingExpanded = expandedBillingUnits[billingUnitCompositeKey];
                        return (
                          <div
                            key={billingUnitCompositeKey}
                            style={{
                              marginTop: "8px",
                              border: "1px solid #e8e8e8",
                              borderRadius: "6px",
                              overflow: "hidden",
                            }}
                          >
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => this.toggleBillingUnit(allocationKey, billingUnitKey)}
                              onKeyDown={(e) => e.key === "Enter" && this.toggleBillingUnit(allocationKey, billingUnitKey)}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "10px 12px",
                                backgroundColor: "#fff",
                                cursor: "pointer",
                                userSelect: "none",
                              }}
                            >
                              <TagsOutlined style={{ marginRight: "8px", fontSize: "13px", color: "#8c8c8c" }} />
                              {isBillingExpanded ? (
                                <DownOutlined style={{ marginRight: "8px", fontSize: "12px" }} />
                              ) : (
                                <RightOutlined style={{ marginRight: "8px", fontSize: "12px" }} />
                              )}
                              <span style={{ flex: 1 }}>{billingUnitDescription}</span>
                              <span>{this.formatCurrency(buAmount)}</span>
                            </div>

                            {/* Layer 3: Line items table (when billing unit expanded) */}
                            {isBillingExpanded && (
                                <div style={{ padding: "12px", backgroundColor: "#fafafa" }}>
                                  <Table
                                    key={billingUnitCompositeKey}
                                    columns={this.getLineItemColumns(lineItems, searchTerm)}
                                    dataSource={lineItems}
                                    rowKey={this.getLineItemRowKey}
                                    rowClassName={this.getLineItemRowClassName}
                                    locale={this.getTableFilterLocale()}
                                    pagination={this.getBillingUnitTablePagination(billingUnitCompositeKey)}
                                    size="small"
                                    scroll={{ x: true }}
                                  />
                                </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            </div>
            {hasInvoices && totalAllocations > allocationPageSize && (
              <div style={{ display: "flex", justifyContent: "center", marginTop: "16px", marginBottom: "24px" }}>
                <Pagination
                  current={allocationPage}
                  pageSize={allocationPageSize}
                  total={totalAllocations}
                  showSizeChanger
                  pageSizeOptions={["10", "15", "25", "50"]}
                  showTotal={(total) => `${t.CONDO_INVOICES_OF || "of"} ${total} ${t.CONDO_INVOICES_ENTRIES || "entries"}`}
                  onChange={(page, pageSize) =>
                    this.setState({
                      allocationPage: page,
                      allocationPageSize: pageSize || allocationPageSize,
                    })
                  }
                />
              </div>
            )}
          </>
        )}

        <style>{`
          .condo-invoice-row-match {
            background-color: #fffbe6 !important;
          }
          .condo-invoice-search-highlight {
            background-color: #ffc069;
            padding: 0 1px;
            border-radius: 2px;
          }
          /* Pagination "rows per page" dropdown above sidebar and other UI */
          .ant-select-dropdown {
            z-index: 9999 !important;
          }
          .sidebar-aware-modal {
            padding-left: 200px !important;
          }
          .sidebar-aware-modal .ant-modal {
            max-width: calc(100vw - 240px);
          }
        `}</style>
        <Modal
          title={t.CONDO_INVOICE_PREVIEW}
          open={previewModalVisible}
          onCancel={this.handleClosePreview}
          wrapClassName="sidebar-aware-modal"
          width="90%"
          style={{ top: 20 }}
          footer={[
            <Button key="close" onClick={this.handleClosePreview}>
              {t.CONDO_INVOICE_CLOSE}
            </Button>
          ]}
        >
          {previewLoading ? (
            <div style={{ textAlign: "center", padding: "50px" }}>
              <Spin size="large" />
              <p style={{ marginTop: "20px" }}>{t.CONDO_INVOICE_PDF_LOADING}</p>
            </div>
          ) : previewError ? (
            <Alert
              message={previewError}
              type="error"
              style={{ margin: "20px 0" }}
            />
          ) : previewPdfBlobs?.length ? (
            <>
              {previewPartialErrors?.length > 0 && (
                <Alert
                  message={t.CONDO_INVOICE_PARTIAL_PREVIEW != null ? t.CONDO_INVOICE_PARTIAL_PREVIEW : `${previewPdfBlobs.length} of ${previewPdfBlobs.length + previewPartialErrors.length} document(s) available`}
                  type="info"
                  style={{ marginBottom: "12px" }}
                />
              )}
              {previewPdfBlobs.length > 1 && (
                <div style={{ marginBottom: "12px" }}>
                  <Select
                    value={previewSelectedIndex}
                    onChange={this.handlePreviewSelectDocument}
                    style={{ minWidth: 200 }}
                    options={previewPdfBlobs.map(({ fileName }, i) => ({
                      label: fileName || `Document ${i + 1}`,
                      value: i,
                    }))}
                  />
                </div>
              )}
              <iframe
                src={previewPdfBlobs[previewSelectedIndex]?.url}
                style={{
                  width: "100%",
                  height: "80vh",
                  border: "none",
                }}
                title="Invoice PDF Preview"
              />
            </>
          ) : null}
        </Modal>
      </div>
    );
  }
}

export default inject("contractStore")(
  withRouter(observer(CondoInvoicesList))
);

